import ErrorCard from '@app/features/oidc/components/ErrorCard';
import { captureError, captureMessage } from '@app/lib/Sentry';
import { clearGlobalSearchPrefs } from '@app/features/globalSearch/prefs';
import { OIDC_TOKEN_KEY } from '@feat-auth/auth.config';
import { Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';

export const ProtectedWrapper = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();
  const hasTriggeredLoginRef = useRef(false);
  // Guards against launching multiple recovery attempts for the same error.
  const isRecoveringRef = useRef(false);
  // Set only when recovery is fully exhausted (silent renew AND redirect both
  // failed), at which point we surface the manual re-login card.
  const [recoveryFailed, setRecoveryFailed] = useState(false);

  // Initial login: no session, nothing in flight, and crucially no error — the
  // error case is owned by the recovery effect below so we don't double-trigger.
  useEffect(() => {
    if (
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.error &&
      !isRecoveringRef.current &&
      !hasTriggeredLoginRef.current
    ) {
      hasTriggeredLoginRef.current = true;
      // Delay to ensure we're not in the middle of a render
      setTimeout(() => {
        auth.signinRedirect();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.isLoading, auth.error]);

  // Gracefully recover from token-renewal failures. The most common trigger is
  // Keycloak returning a changed `auth_time` in the refreshed id_token, which
  // oidc-client-ts rejects with "auth_time in id_token does not match original
  // auth_time" during automatic silent renew. A fresh silent sign-in is treated
  // as a brand-new authentication (no comparison against the prior id_token), so
  // it both clears the error and re-baselines the tokens for future renewals.
  // If the silent attempt fails we fall back to a full redirect, and only show
  // the error card if even that cannot be initiated.
  useEffect(() => {
    if (!auth.error || isRecoveringRef.current) {
      return;
    }

    isRecoveringRef.current = true;
    captureError(auth.error instanceof Error ? auth.error : new Error(String(auth.error)), {
      context: 'ProtectedWrapper authentication error — attempting silent recovery',
    });

    void (async () => {
      // An authorization code is single-use, and react-oidc-context only strips it
      // from the URL after a callback succeeds. Left in place, every reload retries
      // the spent code and fails with "No matching state found in storage".
      if (hasAuthParams()) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // signinSilent and signinRedirect are wrapped by react-oidc-context: they
      // report failure by dispatching onto auth.error and resolving with null, and
      // never reject. Failure has to be read from the return value — a catch here
      // would never run.
      const recoveredUser = await auth.signinSilent();
      if (recoveredUser) {
        // USER_LOADED clears auth.error and re-authenticates; recovery is invisible.
        isRecoveringRef.current = false;
        return;
      }

      // A breadcrumb on the error already reported above, not a second incident: a
      // synthetic Error here would open its own Sentry issue with a stack pointing at
      // this line rather than at whatever actually failed.
      captureMessage('Silent recovery failed — redirecting to login', 'warning');

      localStorage.removeItem(OIDC_TOKEN_KEY);
      clearGlobalSearchPrefs();
      await auth.signinRedirect();

      // A successful redirect unloads the page, so reaching this line means the
      // redirect never happened. isRecoveringRef stays latched: each failed attempt
      // dispatches a fresh error object, and releasing it here would re-trigger this
      // effect on that new identity and spin forever.
      setRecoveryFailed(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.error]);

  // Recovery exhausted — let the user re-authenticate manually.
  if (recoveryFailed) {
    return (
      <ErrorCard
        onClick={() => {
          localStorage.removeItem(OIDC_TOKEN_KEY);
          clearGlobalSearchPrefs();
          hasTriggeredLoginRef.current = false;
          isRecoveringRef.current = false;
          setRecoveryFailed(false);
          auth.signinRedirect();
        }}
      />
    );
  }

  // While a session is being established or recovered, show a spinner instead of
  // the error card. If we still hold a valid session, fall through and keep
  // rendering the app so a background silent recovery stays seamless.
  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <div className="justify-center items-center w-screen h-screen flex">
        <Spin spinning={true} />
      </div>
    );
  }

  // Store token when authenticated (for backward compatibility if other parts of the app need it)
  if (auth.user?.access_token) {
    localStorage.setItem(OIDC_TOKEN_KEY, auth.user.access_token);
  }
  return <>{children}</>;
};
